
import type { ReportColumnsSpec, ReportMetricRow } from "@core/components/ReportTreeTable";

export type ApiReportColumn = {
  headerName: string;
  field?: string;
  children?: ApiReportColumn[];
};

// (district/nation/church) where some rows are group markers
// - district row: district_name present, church_name/template_name null
// - church row: church_name present, template_name null
// - template row: template_name present
export type ApiReportRow = Record<string, any>;

type TreeKind = "district" | "nation" | "church";

/**
 * Converts API `columns` to the spec expected by ReportTreeTable.
 * Assumptions:
 * - Any column with children is a grouped metric column.
 * - Any column with NO children and a field is a “name-ish” column (district/church/template),
 *    we exclude those from metric groups.
 */
export function toTreeColumnsSpec(apiColumns: ApiReportColumn[]): ReportColumnsSpec {
  const groups = apiColumns
    .filter((c) => Array.isArray(c.children) && c.children.length > 0)
    .map((group) => ({
      header: group.headerName,
      columns: (group.children ?? [])
        .filter((leaf) => !!leaf.field)
        .map((leaf) => ({
          id: leaf.field as string,
          header: leaf.headerName,
          field: leaf.field as string,
        })),
    }));

 
  return { nameHeader: "", groups };
}

/**
 * Parent-entity rows (church or district) with nested templates.
 * - parent header: name field present, template_name null
 * - template rows: template_name present
 */
export function buildEntityParentTree(
  rows: ApiReportRow[],
  parentIdField: "church_id" | "district_id",
  parentNameField: "church_name" | "district_name"
): ReportMetricRow[] {
  const result: ReportMetricRow[] = [];
  const idPrefix = parentIdField === "district_id" ? "district" : "church";

  let currentParent: ReportMetricRow | null = null;

  for (let i = 0; i < rows.length; i++) {
    const r = rows[i];

    const parentId = r[parentIdField] ?? null;
    const parentName = r[parentNameField] ?? null;
    const templateId = r.template_id ?? null;
    const templateName = r.template_name ?? null;

    if (parentName && !templateName) {
      currentParent = {
        id: `${idPrefix}:${parentId ?? parentName}:${i}`,
        name: String(parentName),
        ...copyMetrics(r),
        __rowType: "entity",
        subRows: [],
      };
      result.push(currentParent);
      continue;
    }

    if (templateName) {
      const leaf: ReportMetricRow = {
        id: `template:${templateId ?? templateName}:${i}`,
        name: String(templateName),
        ...copyMetrics(r),
        __rowType: "template",
      };

      if (!currentParent) {
        result.push(leaf);
      } else {
        currentParent.subRows = currentParent.subRows ?? [];
        currentParent.subRows.push(leaf);
      }
    }
  }

  for (const n of result) {
    if (!n.subRows) n.subRows = [];
  }

  return result;
}

/**
 * District endpoint rows:
 * - church header row: church_name is present, template_name null
 * - template rows: template_name present
 * Collapsible: Church -> Templates
 */
export function buildDistrictTree(rows: ApiReportRow[]): ReportMetricRow[] {
  return buildEntityParentTree(rows, "church_id", "church_name");
}

/**
 * Nation district-origin office rollup:
 * - district header row: district_name present, template_name null
 * - template rows: template_name present
 * Collapsible: District -> Templates
 */
export function buildDistrictOriginTree(rows: ApiReportRow[]): ReportMetricRow[] {
  return buildEntityParentTree(rows, "district_id", "district_name");
}

/**
 * Nation endpoint rows:
 * - district marker row: district_name present, church_name/template_name null
 * - church marker row: church_name present, template_name null (district_name null)
 * - template leaf row: template_name present (district_name/church_name null)
 *
 * Collapsible: District -> Churches -> Templates
 */
export function buildNationTree(rows: ApiReportRow[]): ReportMetricRow[] {
  const result: ReportMetricRow[] = [];

  let currentDistrict: ReportMetricRow | null = null;
  let currentChurch: ReportMetricRow | null = null;

  for (let i = 0; i < rows.length; i++) {
    const r = rows[i];

    const districtId = r.district_id ?? null;
    const districtName = r.district_name ?? null;

    const churchId = r.church_id ?? null;
    const churchName = r.church_name ?? null;

    const templateId = r.template_id ?? null;
    const templateName = r.template_name ?? null;

    // District group marker
    if (districtName && !churchName && !templateName) {
      currentDistrict = {
        id: `district:${districtId ?? districtName}:${i}`,
        name: String(districtName),
        ...copyMetrics(r),
        __rowType: "entity",
        subRows: [],
      };
      result.push(currentDistrict);
      currentChurch = null;
      continue;
    }

    // Church group marker under current district
    if (churchName && !templateName) {
      const churchNode: ReportMetricRow = {
        id: `church:${churchId ?? churchName}:${i}`,
        name: String(churchName),
        ...copyMetrics(r),
        __rowType: "entity",
        subRows: [],
      };

      if (!currentDistrict) {
        // Defensive fallback
        result.push(churchNode);
        currentDistrict = null;
      } else {
        currentDistrict.subRows = currentDistrict.subRows ?? [];
        currentDistrict.subRows.push(churchNode);
      }

      currentChurch = churchNode;
      continue;
    }

    // Template leaf under current church
    if (templateName) {
      const leaf: ReportMetricRow = {
        id: `template:${templateId ?? templateName}:${i}`,
        name: String(templateName),
        ...copyMetrics(r),
        __rowType: "template",
      };

      if (currentChurch) {
        currentChurch.subRows = currentChurch.subRows ?? [];
        currentChurch.subRows.push(leaf);
      } else if (currentDistrict) {
        // Fallback: template rows without church marker (still nest under district)
        currentDistrict.subRows = currentDistrict.subRows ?? [];
        currentDistrict.subRows.push(leaf);
      } else {
        // Last resort: flat
        result.push(leaf);
      }
    }
  }

  // Ensure parents have subRows to be expandable
  for (const d of result) {
    if (!d.subRows) d.subRows = [];
    for (const c of d.subRows ?? []) {
      if (!c.subRows) c.subRows = [];
    }
  }

  return result;
}

/**
 * Church endpoint rows:
 * - template rows are top-level
 * - sub-group rows, when present, nest under their template
 *
 * Collapsible: Template -> Sub Groups
 */
export function buildChurchTree(rows: ApiReportRow[]): ReportMetricRow[] {
  const result: ReportMetricRow[] = [];
  const parentsByTemplateKey = new Map<string, ReportMetricRow>();
  let currentParent: ReportMetricRow | null = null;

  for (let i = 0; i < rows.length; i++) {
    const row = rows[i];
    const templateId = row.template_id ?? row.report_template_id ?? null;
    const templateName = row.template_name ?? row.report_template_name ?? null;
    const templateKey =
      templateId !== null || templateName !== null ? String(templateId ?? templateName) : null;

    const subGroupId = row.report_sub_group_id ?? row.sub_group_id ?? null;
    const subGroupName = row.sub_group_name ?? null;
    const isSubGroup =
      row.is_sub_group === true ||
      subGroupId !== null ||
      (typeof subGroupName === "string" && subGroupName.trim() !== "");

    if (!isSubGroup) {
      const existingParent = templateKey ? parentsByTemplateKey.get(templateKey) : undefined;
      const nextParent: ReportMetricRow = existingParent ?? {
        id: `template:${templateKey ?? i}:${i}`,
        name: String(templateName ?? `Template ${templateId ?? ""}`),
        subRows: [],
        __rowType: "template",
      };

      nextParent.id = `template:${templateKey ?? i}:${i}`;
      nextParent.name = String(templateName ?? `Template ${templateId ?? ""}`);
      Object.assign(nextParent, copyMetrics(row));
      nextParent.__rowType = "template";
      nextParent.subRows = nextParent.subRows ?? [];

      if (!existingParent) {
        result.push(nextParent);
        if (templateKey) parentsByTemplateKey.set(templateKey, nextParent);
      }

      currentParent = nextParent;
      continue;
    }

    const childRow: ReportMetricRow = {
      id: `subgroup:${templateKey ?? "unknown"}:${subGroupId ?? subGroupName ?? i}:${i}`,
      name: String(subGroupName ?? `Subgroup ${subGroupId ?? ""}`),
      ...copyMetrics(row),
      __rowType: "sub_group",
    };

    const parentFromKey: ReportMetricRow | null =
      templateKey ? parentsByTemplateKey.get(templateKey) ?? null : null;
    const parentRow: ReportMetricRow | null =
      parentFromKey ?? (templateKey === null ? currentParent : null);

    if (parentRow) {
      parentRow.subRows = parentRow.subRows ?? [];
      parentRow.subRows.push(childRow);

      if (templateKey && !parentsByTemplateKey.has(templateKey)) {
        parentsByTemplateKey.set(templateKey, parentRow);
      }

      currentParent = parentRow;
      continue;
    }

    // Defensive fallback for subgroup rows that arrive without a matching template row.
    if (templateKey) {
      const syntheticParent: ReportMetricRow = {
        id: `template:${templateKey}:${i}`,
        name: String(templateName ?? `Template ${templateId ?? ""}`),
        subRows: [childRow],
        __rowType: "template",
      };
      result.push(syntheticParent);
      parentsByTemplateKey.set(templateKey, syntheticParent);
      currentParent = syntheticParent;
      continue;
    }

    result.push({
      id: `orphan:${i}`,
      name: String(subGroupName ?? templateName ?? "Unknown"),
      ...copyMetrics(row),
    });
    currentParent = null;
  }

  return result;
}

/**
 * Helper: copy metric fields only.
 * exclude identity-ish fields so the tree table’s name column is the only label.
 */
function copyMetrics(row: ApiReportRow): Record<string, unknown> {
  const omit = new Set([
    // "district_id",
    "district_name",
    // "church_id",
    "church_name",
    // "template_id",
    "template_name",
    "report_sub_group_id",
    "sub_group_id",
    "sub_group_name",
    "is_sub_group",
  ]);

  const out: Record<string, unknown> = {};

  out.__meta = {
    district_id: row.district_id ?? null,
    church_id: row.church_id ?? null,
    template_id: row.template_id ?? row.report_template_id ?? null,
    report_sub_group_id: row.report_sub_group_id ?? row.sub_group_id ?? null,
    sub_group_name: row.sub_group_name ?? null,
    is_sub_group: row.is_sub_group === true,
    // department_report_id: row.department_report_id ?? null,
    // department_report_template_id: row.department_report_template_id ?? null,
  };
  


  for (const [k, v] of Object.entries(row)) {
    if (omit.has(k)) continue;
    out[k] = v;
  }
  return out;
}

/**
 * Convenience: decide which builder to use based on scope.
 */
export function buildTreeByScope(kind: TreeKind, rows: ApiReportRow[]): ReportMetricRow[] {
  if (kind === "nation") return buildNationTree(rows);
  if (kind === "district") return buildDistrictTree(rows);
  return buildChurchTree(rows);
}

export function buildTreeForSection(
  sectionType: "office" | "district_origin" | "children",
  scope: TreeKind,
  rows: ApiReportRow[]
): ReportMetricRow[] {
  if (sectionType === "office") return buildChurchTree(rows);
  if (sectionType === "district_origin") return buildDistrictOriginTree(rows);
  return buildTreeByScope(scope, rows);
}
